import React, { FC, useReducer, useCallback, useState } from 'react';
import { action } from 'typesafe-actions';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { isEmpty, isDefined } from '@dxb-web-ui-framework/common/utils';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { CheckMandateResponseBody } from '../../domain/Authorization/entities/CheckMandateResponseDTO';
import { transactionService, authorizationService } from '../../../../../channels/secure-web/services';
import { TransactionsActionTypes, TransactionsProviderProps } from './types';
import { defaultTransactionsProviderState } from './defaultState';
import { TransactionsReducer } from './TransactionsReducer';
import { TransactionsContext } from '.';

const TransactionsProvider: FC<TransactionsProviderProps> = ({ children, transactionsList }) => {
  const [state, dispatch] = useReducer(TransactionsReducer, defaultTransactionsProviderState);
  const [timestamp] = useState(new Date().toISOString());

  const shouldFetchTransactionDetails =
    !isEmpty(state.selectedTransaction) &&
    isDefined(state.selectedTransaction?.orderId) &&
    isDefined(state.selectedTransaction?.accountNumber);

  const {
    data: transactionDetails,
    error: transactionDetailsError,
    isLoading: isLoadingTransactionDetails,
    mutate: transactionDetailsMutator,
  } = useInvokeSWRService(
    transactionService.GetConsolidatedTransactionDetails,
    {
      orderId: state.selectedTransaction?.orderId,
      accountNumber: state.selectedTransaction?.accountNumber,
      meta: {
        timestamp: state.selectedTransaction?.timestamp ?? timestamp,
      },
    },
    shouldFetchTransactionDetails,
  );

  const shouldCheckAuthorization =
    Boolean(state.selectedTransaction) && state.selectedTransaction !== null && Boolean(transactionDetails);

  // Check Authorization
  const {
    data: checkAuthorizationData,
    isLoading: isLoadingCheckAuthorization,
    error: checkAuthorizationError,
    mutate: checkAuthorizationMutation,
  } = useInvokeSWRService<CheckMandateResponseBody>(
    authorizationService.CheckMandate,
    {
      accountNumber: transactionDetails?.fromAccount,
      uniqueId: Number(transactionDetails?.transactionId),
      amount: Number(transactionDetails?.amount),
      meta: {
        timestamp: state.selectedTransaction?.timestamp ?? timestamp,
      },
    },
    shouldCheckAuthorization,
  );

  const onSelectTransaction = useCallback(
    (orderId: Nullable<number>) => {
      const foundTransaction = transactionsList?.find(tr => tr.orderId === orderId);
      dispatch(
        action(
          TransactionsActionTypes.setSelectedTransaction,
          { ...foundTransaction, timestamp: new Date().toISOString() } || null,
        ),
      );
    },
    [transactionsList],
  );

  const onRefetchTransactionDetails = useCallback(() => {
    transactionDetailsMutator && transactionDetailsMutator();
  }, [transactionDetailsMutator]);

  const onCheckAuthorizationMutation = useCallback(() => {
    checkAuthorizationMutation && checkAuthorizationMutation();
  }, [checkAuthorizationMutation]);

  const values = {
    ...state,
    checkAuthorizationData,
    transactionsList: transactionsList || [],
    transactionDetails: transactionDetails || null,
    onCheckAuthorizationMutation,
    actions: {
      onSelectTransaction,
      onRefetchTransactionDetails,
    },
    loadingState: {
      isLoadingTransactionDetails,
      isLoadingCheckAuthorization,
    },
    errorState: {
      transactionDetailsError,
      checkAuthorizationError,
    },
  };

  return <TransactionsContext.Provider value={values}>{children}</TransactionsContext.Provider>;
};

export { TransactionsProvider };
